import React, { useState, useRef } from "react";
import {
  User,
  Camera,
  Save,
  Edit3,
  Calendar,
  Star,
  Award,
  Clock,
  MessageSquare,
  Phone,
  Video,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import type { CounselorProfile } from "@/types";
import {
  useCurrentCounselorProfile,
  useUpdateCurrentCounselorProfile,
} from "@/hooks/useCounselorData";
import { cn } from "@/lib/utils";
import {
  MultiSelect,
  type Option,
} from "@/components/ui/multi-select-combobox";
import {
  uploadProfileImageViaProxy,
  validateImageFile,
} from "@/services/storage";

interface ProfileFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  specializations: string[];
  bio: string;
  experienceYears: number;
  qualification: string;
  languages: string[];
  hourlyRate: number;
  isAvailable: boolean;
}

export const ProfilePage: React.FC = () => {
  const { user, profilePictureUrl, updateProfile } = useAuth();
  const {
    data: counselorProfile,
    isLoading,
    refetch: refetchProfile,
  } = useCurrentCounselorProfile();
  const updateProfileMutation = useUpdateCurrentCounselorProfile();
  const [isEditing, setIsEditing] = useState(false);

  // Image upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<ProfileFormData>({
    firstName: counselorProfile?.firstName || "",
    lastName: counselorProfile?.lastName || "",
    email: counselorProfile?.email || user?.email || "",
    phone: counselorProfile?.phoneNumber || user?.phoneNumber || "",
    specializations: counselorProfile?.specialization
      ? counselorProfile.specialization.split(", ").filter(Boolean)
      : [],
    bio: counselorProfile?.bio || "",
    experienceYears: counselorProfile?.experienceYears || 0,
    qualification: counselorProfile?.qualification || "",
    languages: counselorProfile?.languages || [],
    hourlyRate: 0, // Not in backend yet
    isAvailable: counselorProfile?.isAvailable ?? false,
  });

  // Update form data when profile loads
  React.useEffect(() => {
    if (counselorProfile && !isLoading) {
      setFormData({
        firstName: counselorProfile.firstName || "",
        lastName: counselorProfile.lastName || "",
        email: counselorProfile.email || user?.email || "",
        phone: counselorProfile.phoneNumber || user?.phoneNumber || "",
        specializations: counselorProfile.specialization
          ? counselorProfile.specialization.split(", ").filter(Boolean)
          : [],
        bio: counselorProfile.bio || "",
        experienceYears: counselorProfile.experienceYears || 0,
        qualification: counselorProfile.qualification || "",
        languages: counselorProfile.languages || [],
        hourlyRate: 0, // Not in backend yet
        isAvailable: counselorProfile.isAvailable ?? false,
      });
    }
  }, [counselorProfile, user, isLoading]);

  // Refetch profile after page load to get updated availability status
  React.useEffect(() => {
    const timer = setTimeout(() => {
      refetchProfile();
    }, 3000); // Refetch after 3 seconds

    return () => clearTimeout(timer);
  }, [refetchProfile]);

  // Mock profile stats
  const profileStats = {
    totalSessions: counselorProfile?.totalSessions || 0,
    totalChatSessions: (counselorProfile as any)?.totalChatSessions || 0,
    totalAudioCallSessions: (counselorProfile as any)?.totalAudioCallSessions || 0,
    totalVideoCallSessions: (counselorProfile as any)?.totalVideoCallSessions || 0,
    averageRating: counselorProfile?.rating || 0,
    totalStudents: 42,
    responseTime: "12 minutes",
    joinedDate: "2023-08-15",
    completionRate: 96,
    totalEarnings: 15750,
  };

  const specializationOptions: Option[] = [
    { value: "MBA", label: "MBA" },
    { value: "MBBS India", label: "MBBS India" },
    { value: "MBBS Abroad", label: "MBBS Abroad" },
    { value: "BTECH", label: "BTECH" },
    { value: "BBA", label: "BBA" },
    { value: "BCOM", label: "BCOM" },
  ];

  const languageOptions = [
    "English",
    "Spanish",
    "French",
    "German",
    "Chinese",
    "Hindi",
    "Portuguese",
    "Arabic",
  ];

  const handleInputChange = (field: keyof ProfileFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLanguageToggle = (language: string) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.includes(language)
        ? prev.languages.filter((l) => l !== language)
        : [...prev.languages, language],
    }));
  };

  const handleSave = async () => {
    try {
      setImageUploadError(null);
      let profileData: Partial<CounselorProfile> = {
        ...formData,
        phoneNumber: formData.phone, // Map phone to phoneNumber
      };
      delete (profileData as any).phone; // Remove the phone field
      // Map specializations array to a single string if backend expects one
      if ((profileData as any).specializations) {
        (profileData as any).specialization =
          formData.specializations.join(", ");
        delete (profileData as any).specializations;
      }

      // Upload image if selected
      let uploadResult:
        | { success: boolean; fileId?: string; error?: string }
        | undefined;
      if (selectedFile) {
        setIsImageUploading(true);
        uploadResult = await uploadProfileImageViaProxy(selectedFile);

        if (!uploadResult.success) {
          setImageUploadError(uploadResult.error || "Failed to upload image");
          setIsImageUploading(false);
          return;
        }

        // Add the uploaded file ID to the profile data
        profileData = { ...profileData, profilePicture: uploadResult.fileId };
      }

      const updatedProfile = await updateProfileMutation.mutateAsync(
        profileData
      );

      // Update auth context with new profile data (this will auto-refresh profile picture)
      updateProfile(updatedProfile);

      // Refetch profile to ensure we have the latest data with resolved URLs
      await refetchProfile();

      // If image was uploaded, clear upload state
      if (selectedFile && uploadResult?.fileId) {
        // Clear selected file and preview
        setSelectedFile(null);
        setImagePreview(null);
      }

      setIsEditing(false);
      setIsImageUploading(false);
      console.log("Profile updated:", profileData);
    } catch (error) {
      console.error("Failed to update profile:", error);
      setIsImageUploading(false);
      // You can add a toast notification here
    }
  };

  const handleCancel = () => {
    // Reset form data
    setFormData({
      firstName: counselorProfile?.firstName || "",
      lastName: counselorProfile?.lastName || "",
      email: counselorProfile?.email || user?.email || "",
      phone: counselorProfile?.phoneNumber || user?.phoneNumber || "",
      specializations: counselorProfile?.specialization
        ? counselorProfile.specialization.split(", ").filter(Boolean)
        : [],
      bio: counselorProfile?.bio || "",
      experienceYears: counselorProfile?.experienceYears || 0,
      qualification: counselorProfile?.qualification || "",
      languages: counselorProfile?.languages || [],
      hourlyRate: counselorProfile?.hourlyRate || 0,
      isAvailable: counselorProfile?.isAvailable ?? false,
    });

    // Reset image state
    setSelectedFile(null);
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImagePreview(null);
    setImageUploadError(null);

    setIsEditing(false);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file
      const validation = validateImageFile(file);
      if (!validation.isValid) {
        setImageUploadError(validation.errors.join(", "));
        return;
      }

      // Clear previous preview URL to prevent memory leaks
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }

      setSelectedFile(file);
      setImageUploadError(null);

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
          <p className="text-gray-600 mt-1">
            Manage your professional information and settings
          </p>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors mt-4 sm:mt-0"
          >
            <Edit3 className="h-4 w-4" />
            <span>Edit Profile</span>
          </button>
        ) : (
          <div className="flex space-x-3 mt-4 sm:mt-0">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isImageUploading}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>{isImageUploading ? "Uploading..." : "Save Changes"}</span>
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              Basic Information
            </h2>

            {/* Profile Picture */}
            <div className="flex items-center space-x-6 mb-6">
              <div className="relative">
                <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center overflow-hidden">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Profile Preview"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : profilePictureUrl ? (
                    <img
                      src={profilePictureUrl}
                      alt="Profile"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <User className="h-8 w-8 text-white" />
                  )}
                </div>
                {isEditing && (
                  <label className="absolute -bottom-1 -right-1 p-1 bg-primary text-white rounded-full cursor-pointer hover:bg-primary/90">
                    <Camera className="h-4 w-4" />
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium text-gray-900">
                  {formData.firstName} {formData.lastName}
                </h3>
                <p className="text-gray-600">
                  {formData.specializations.join(", ")}
                </p>
                <div className="flex items-center mt-1">
                  <div
                    className={cn(
                      "w-2 h-2 rounded-full mr-2",
                      formData.isAvailable ? "bg-green-500" : "bg-red-500"
                    )}
                  ></div>
                  <span
                    className={cn(
                      "text-sm",
                      formData.isAvailable ? "text-green-600" : "text-red-600"
                    )}
                  >
                    {formData.isAvailable ? "Available" : "Unavailable"}
                  </span>
                </div>
                {imageUploadError && (
                  <p className="text-sm text-red-600 mt-2">
                    {imageUploadError}
                  </p>
                )}
                {isImageUploading && (
                  <p className="text-sm text-blue-600 mt-2">
                    Uploading image...
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleInputChange("firstName", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleInputChange("lastName", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.lastName}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email
                </label>
                {isEditing ? (
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">{formData.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone
                </label>
                <p className="text-gray-900">{formData.phone}</p>
                {isEditing && (
                  <p className="text-xs text-gray-500 mt-1">
                    Contact support to change phone number
                  </p>
                )}
              </div>

              {/* Specializations */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Specializations
                </label>
                {isEditing ? (
                  <MultiSelect
                    options={specializationOptions}
                    selected={formData.specializations}
                    onChange={(values) =>
                      handleInputChange("specializations", values)
                    }
                    placeholder="Select specializations"
                  />
                ) : (
                  <p className="text-gray-900">
                    {formData.specializations.join(", ")}
                  </p>
                )}
              </div>

              {/* Experience */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Experience (Years)
                </label>
                {isEditing ? (
                  <input
                    type="number"
                    min="0"
                    value={formData.experienceYears}
                    onChange={(e) =>
                      handleInputChange(
                        "experienceYears",
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                ) : (
                  <p className="text-gray-900">
                    {formData.experienceYears} years
                  </p>
                )}
              </div>
            </div>

            {/* Bio */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bio
              </label>
              {isEditing ? (
                <textarea
                  rows={4}
                  value={formData.bio}
                  onChange={(e) => handleInputChange("bio", e.target.value)}
                  placeholder="Tell students about yourself, your approach, and your expertise..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">
                  {formData.bio || "No bio provided"}
                </p>
              )}
            </div>

            {/* Qualification */}
            {/* <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Qualification</label>
              {isEditing ? (
                <input
                  type="text"
                  value={formData.qualification}
                  onChange={(e) => handleInputChange('qualification', e.target.value)}
                  placeholder="e.g., Ph.D. in Psychology, M.Ed. in Counseling"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              ) : (
                <p className="text-gray-900">{formData.qualification}</p>
              )}
            </div> */}

            {/* Languages */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Languages
              </label>
              {isEditing ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {languageOptions.map((language) => (
                    <label
                      key={language}
                      className="flex items-center space-x-2"
                    >
                      <input
                        type="checkbox"
                        checked={formData.languages.includes(language)}
                        onChange={() => handleLanguageToggle(language)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-700">{language}</span>
                    </label>
                  ))}
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {formData.languages.map((language) => (
                    <span
                      key={language}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {language}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Availability & Rate */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Availability Status</label>
                {isEditing ? (
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={formData.isAvailable}
                      onChange={(e) => handleInputChange('isAvailable', e.target.checked)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Available for new sessions</span>
                  </label>
                ) : (
                  <p className={cn(
                    "text-sm font-medium",
                    formData.isAvailable ? "text-green-600" : "text-red-600"
                  )}>
                    {formData.isAvailable ? 'Available for new sessions' : 'Not accepting new sessions'}
                  </p>
                )}
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hourly Rate
                  <button
                    onClick={() => setShowRates(!showRates)}
                    className="ml-2 p-1 text-gray-400 hover:text-gray-600"
                  >
                    {showRates ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </label>
                {isEditing ? (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      value={formData.hourlyRate}
                      onChange={(e) => handleInputChange('hourlyRate', parseFloat(e.target.value) || 0)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                ) : (
                  <p className="text-gray-900">
                    {showRates ? `$${formData.hourlyRate}/hour` : '••••'}
                  </p>
                )}
              </div> */}
            </div>
          </div>
        </div>

        {/* Stats & Achievements */}
        <div className="space-y-6">
          {/* Performance Stats */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Performance Stats
            </h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-blue-600" />
                  <span className="text-sm text-gray-600">Total Sessions</span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.totalSessions}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <MessageSquare className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-600">Total Chat Sessions</span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.totalChatSessions}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-purple-600" />
                  <span className="text-sm text-gray-600">Total Audio Call Sessions</span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.totalAudioCallSessions}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Video className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-gray-600">Total Video Call Sessions</span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.totalVideoCallSessions}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Star className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm text-gray-600">
                    Average Session Rating
                  </span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.averageRating}/5
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-gray-600">
                    Total Connected Students
                  </span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.totalStudents}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-purple-600" />
                  <span className="text-sm text-gray-600">
                    Session Response Time
                  </span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.responseTime}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Award className="h-4 w-4 text-orange-600" />
                  <span className="text-sm text-gray-600">
                    Session Completion Rate
                  </span>
                </div>
                <span className="font-medium text-gray-900">
                  {profileStats.completionRate}%
                </span>
              </div>

              {/* {showRates && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                  <div className="flex items-center space-x-2">
                    <DollarSign className="h-4 w-4 text-green-600" />
                    <span className="text-sm text-gray-600">Total Earnings</span>
                  </div>
                  <span className="font-medium text-gray-900">${profileStats.totalEarnings.toLocaleString()}</span>
                </div>
              )} */}
            </div>
          </div>

          {/* Account Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Account Information
            </h3>
            <div className="space-y-3">
              <div>
                <span className="text-sm text-gray-600">Member Since</span>
                <p className="font-medium text-gray-900">
                  {new Date(profileStats.joinedDate).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }
                  )}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-600">Account Status</span>
                <p className="font-medium text-green-600">Active & Verified</p>
              </div>
              <div>
                <span className="text-sm text-gray-600">User ID</span>
                <p className="font-medium text-gray-900 font-mono text-sm">
                  {user?.id}
                </p>
              </div>
            </div>
          </div>

          {/* Recent Achievements */}
          {/* <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Achievements</h3>
            <div className="space-y-3">
              <div className="flex items-center space-x-3 p-3 bg-yellow-50 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
                <div>
                  <p className="text-sm font-medium text-yellow-900">5-Star Rating</p>
                  <p className="text-xs text-yellow-700">Maintained excellent ratings</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-blue-50 rounded-lg">
                <Award className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Top Performer</p>
                  <p className="text-xs text-blue-700">High completion rate</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 p-3 bg-green-50 rounded-lg">
                <Clock className="h-5 w-5 text-green-600" />
                <div>
                  <p className="text-sm font-medium text-green-900">Quick Responder</p>
                  <p className="text-xs text-green-700">Fast response times</p>
                </div>
              </div>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};
